import { useEffect, useState } from 'react';
import {
  Grid, Divider, FormControl, FormControlLabel, Paper,
  FormHelperText, Radio, RadioGroup, Typography, Button,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import ListDetail from "../ListDetail";
import {
  checkEmail, sendCodeEmail, sendCodeTelefono, verificarCodigoContacto, editAvatar, editDatosPersonales,
  editShare, editEmail, verifySavePhone, deleteAccount
} from '../../../actions/users';
import StatusModal from '../../modals/StatusModal';
import CodeModal from '../../modals/CodeModal';
import CrearPassModal from '../../modals/CrearPassModal';
import { loadUserClient, logout } from '../../../actions/auth';
import { recordarDestino } from '../../../utils/destinoAlEntrar';
import { SimpleLoading } from '../../common/Loading';

import { useSelector, useDispatch } from 'react-redux';

const INITIAL_CODE = { user: null, validate: null, type: "EMAIL" };
const INITIAL_MODAL = { show: false, type: "SUCCESS" };

const MainPerfil = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const initialState = {
    name: false,
    tel: false,
    email: false,
    nacimiento: false,
    sexo: false,
    password: false
  };
  const [isEditing, setIsEditing] = useState({
    ...initialState
  });
  const [data, setData] = useState({});
  const [date, setDate] = useState(null);
  const [modalStatus, setModalStatus] = useState(INITIAL_MODAL);
  const [modalCode, setModalCode] = useState(false);
  const [modalPass, setModalPass] = useState(false);
  const [code, setCode] = useState(INITIAL_CODE);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData({
      name: user.name,
      apellido: user?.apellido ?? '',
      tel: user.tel,
      email: user.email,
      nacimiento: user.nacimiento || '',
      sexo: user.sexo || '',
      share: user.share || '0',
      password: ''
    })
  }, [user]);

  const cambiarData = e => { // Cambia el valor de "data" en el input editable
    setData({
      ...data,
      [e.target.name]: e.target.value
    })
  }

  const cambiarImg = async e => { //Camia la imagen del del perfil
    e.preventDefault();
    let file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('carpeta', `users/${user._id}`);
      formData.append('nombre', user._id);

      await dispatch(editAvatar(formData));
    }
  }

  const editarDatosPersonales = async e => { // Edita los datos personales del usuario (nombre, apellido, fecha de nacimiento, sexo)
    const info = {
      nombre: data.name,
      apellido: data.apellido,
      sexo: data.sexo,
      birth: date ? moment(date).format('DD-MM-YYYY') : ''
    }

    await dispatch(editDatosPersonales(info, user._id));
    setIsEditing({
      ...initialState
    })
  }

  const editarShare = async e => { // Edita la opción de compartir datos del usuario
    const info = {
      share: e.target.value
    }
    await dispatch(editShare(info, user._id));
  }

  const editarEmail = async e => {
    if (e.key !== 'Enter') return;

    if (data.email == user.email) {
      return setIsEditing({ ...initialState });
    }

    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const emailValido = regex.test(data.email);
    if (!emailValido) return setModalStatus({ type: "ERROR", show: true, message: "El email no es válido" });

    try {
      setLoading(true);

      const emailExistente = await checkEmail(data.email);
      if (emailExistente) return setModalStatus({ type: "ERROR", show: true, message: "Este email ya esta en uso" });

      const info = {
        email: data.email,
        subject: "verificacion",
        nombre: user.name
      };
      const enviado = await sendCodeEmail(info, user._id);

      if (enviado) {
        setCode({
          user: null,
          // El servidor ya no manda el código (lo comprueba él); solo el servidor anterior lo devolvía
          validate: enviado.data?.codigo ?? null,
          type: "EMAIL",
        });
        setModalCode(true);
      } else {
        setModalStatus({
          show: true,
          type: "ERROR",
          message: "Ocurrió un error al enviar el código de verificación",
        });
      }
    } catch (error) {
      console.log("ERROR ", error);
      setModalStatus({
        type: "ERROR",
        show: true,
      });
    } finally {
      setLoading(false);
    }
  }

  const validateCodeEmail = async () => {
    if (!code.user || code.user == "") {
      setLoading(false);
      return setModalStatus({
        show: true,
        type: "ERROR",
        message: "Por favor, ingresa el código que se mandó a tu correo electrónico",
      });
    }

    setLoading(true);
    // El servidor comprueba el código y entrega el permiso para guardar ESE correo
    const verificado = await verificarCodigoContacto({ canal: 'email', dato: data.email, codigo: code.user });
    // Servidor anterior (sin esa ruta): el código venía en la respuesta y se compara aquí, como antes
    const legadoOk = verificado.legado && code.validate != null && Number(code.validate) === Number(code.user);
    if (!verificado.permiso && !legadoOk) {
      setLoading(false);
      return setModalStatus({
        show: true,
        type: "ERROR",
        message: verificado.error || "Código incorrecto. Por favor, intenta de nuevo."
      });
    }

    setModalCode(false);

    const info = {
      email: data.email,
      ...(verificado.permiso ? { permiso: verificado.permiso } : {}),
    }
    try {
      const guardado = await dispatch(editEmail(info, user._id));
      if (!guardado || !guardado.ok) {
        return setModalStatus({ show: true, type: "ERROR", message: guardado?.error || "No se pudo guardar el correo. Inténtalo de nuevo." });
      }
      setModalStatus({
        show: true,
        type: "SUCCESS",
        message: "Correo electrónico actualizado correctamente"
      });
      cancelValidateCode();
    } catch (error) {
      console.log('ERROR ', error);
      setModalStatus({ show: true, type: "ERROR" });
    } finally {
      setLoading(false);
    }
  }

  const validateCodePhone = async () => { // Validar el código de verificación del teléfono
    if (!code.user || code.user == "" || code.user.length < 4) {
      setLoading(false);
      return setModalStatus({
        show: true,
        type: "ERROR",
        message: "Por favor, ingresa el código que se mandó a tu celular",
      });
    }

    try {
      setLoading(true);
      const res = await dispatch(verifySavePhone({ tel: data.tel, code: code.user }, user._id));
      if (res && res.ok) { // Si la verificación fue exitosa
        setLoading(false);
        setModalStatus({
          show: true,
          type: "SUCCESS",
          message: code.confirmar ? "Tu celular quedó confirmado" : "Tu celular quedó actualizado y confirmado"
        });
        cancelValidateCode();
      } else {
        setLoading(false);
        setModalStatus({
          show: true,
          type: "ERROR",
          message: res?.error || "Código incorrecto. Por favor, intenta de nuevo."
        });
      }
    } catch (error) {
      console.log('ERROR ', error);
      setModalStatus({ show: true, type: "ERROR" });
    } finally {
      setLoading(false);
    }
  }

  const cancelValidateCode = () => {
    setIsEditing({
      ...initialState
    });
    setCode(INITIAL_CODE);
    setModalCode(false);
    setLoading(false);
  }

  // Manda el código por SMS y abre el modal para escribirlo. `confirmar`: es el mismo celular que ya tiene la cuenta, sin confirmar
  const mandarCodigoTel = async (tel, confirmar) => {
    try {
      setLoading(true);
      // Antes se pedía como "olvidé mi contraseña" (solo a celulares ya registrados) y sin lada: nunca llegaba al celular nuevo
      const enviado = await sendCodeTelefono(tel);
      if (enviado.ok) {
        if (tel !== data.tel) setData({ ...data, tel });
        setModalCode(true);
        setCode({ ...INITIAL_CODE, type: "PHONE", confirmar });
      } else {
        setModalStatus({
          show: true,
          type: "ERROR",
          message: enviado.error || "Ocurrió un error al enviar el código de verificación",
        });
      }
    } catch (error) {
      console.log("ERROR ", error);
      setModalStatus({
        type: "ERROR",
        show: true,
      });
    } finally {
      setLoading(false);
    }
  }

  const editarTel = async e => { // Editar el teléfono del usuario
    if (e.key !== 'Enter') return;

    // 10 dígitos, sin lada (la lada 52 la pone sendCodeTelefono)
    const tel = String(data.tel || '').replace(/\D/g, '');
    // El mismo número: si ya está confirmado (o no hay ninguno) no hay nada que hacer; si no, escribirlo otra vez es confirmarlo
    if (tel === String(user.tel || '')) {
      if (!tel || user.telVerificado === true) return setIsEditing({ ...initialState });
      return mandarCodigoTel(tel, true);
    }
    if (tel.length !== 10) {
      return setModalStatus({ type: "ERROR", show: true, message: "El celular debe tener 10 dígitos" });
    }
    return mandarCodigoTel(tel, false);
  }

  const confirmarTel = () => { // Confirmar con un código por SMS el celular que ya tiene la cuenta
    const tel = String(user.tel || '').replace(/\D/g, '').slice(-10);
    if (tel.length !== 10) {
      return setModalStatus({ type: "ERROR", show: true, message: "Tu celular guardado no tiene 10 dígitos: cámbialo por el correcto." });
    }
    return mandarCodigoTel(tel, true);
  }

  return (
    <Grid container spacing={1} mb={2} size={12}>
      <SimpleLoading open={loading} />
      <CodeModal
        show={modalCode}
        changeCode={(value) => setCode({ ...code, user: value })}
        validateCode={() => {
          if (code.type == "EMAIL") return validateCodeEmail();
          return validateCodePhone();
        }}
        cancel={cancelValidateCode}
        type={code.type}
        titulo={code.type === "PHONE" && code.confirmar ? "Confirma tu celular" : undefined}
        mensaje={code.type === "PHONE" ? `Te enviamos un código por SMS al +52 ${data.tel}. Escríbelo aquí.` : undefined}
      />
      <CrearPassModal
        show={modalPass}
        handleClose={() => setModalPass(false)}
        setModalStatus={setModalStatus}
        setModalPass={setModalPass}
        // Sin contraseña = la cuenta entra con Google o Facebook: el modal ofrece crear la primera (no pide una "actual")
        tienePassword={Boolean(user?.pass)}
        onGuardada={() => dispatch(loadUserClient())}
        // Al volver a entrar regresa al perfil, para crear ahí la contraseña
        onVolverAEntrar={() => { setModalPass(false); recordarDestino('/mi-perfil'); dispatch(logout('/login')); }}
      />
      <StatusModal
        closedModal={() => setModalStatus(INITIAL_MODAL)}
        show={modalStatus.show}
        type={modalStatus.type}
        message={modalStatus?.message ?? null}
      />

      {/* Header Section */}
      <Grid size={12} container justifyContent="space-between" alignItems="center" mb={3}>
        <Grid>
          <Typography variant="h3" color="primary">
            Datos Personales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Información básica de tu perfil
          </Typography>
        </Grid>
        <Grid hidden={isEditing.name}>
          <Button
            variant="contained"
            startIcon={<DriveFileRenameOutlineIcon />}
            color='secondary'
            onClick={() => {
              // Toggle all edit fields
              const newState = !isEditing.name;
              setIsEditing({
                ...initialState,
                name: newState,
                nacimiento: newState,
                sexo: newState,
              });
            }}
          >
            Editar
          </Button>
        </Grid>
      </Grid>

      {/* List Detail Section */}
      <Paper sx={{ width: '100%' }} >
        <ListDetail
          user={user}
          info={data}
          date={date}
          isEditing={isEditing}
          setDate={setDate}
          setIsEditing={setIsEditing}
          setModalPass={setModalPass}
          cambiarImg={cambiarImg}
          cambiarData={cambiarData}
          editarEmail={editarEmail}
          editarTel={editarTel}
          confirmarTel={confirmarTel}
          editarDatosPersonales={editarDatosPersonales}
        />

        <Grid size={12} mt={3}>
          <Divider />
        </Grid>

        {/* Share Section */}
        <Grid size={12} p={3}>
          <Typography variant="h6" color="text.primary" mb={2}>
            Compartir mi correo y celular con:
          </Typography>
          <FormControl fullWidth>
            <RadioGroup
              row
              aria-labelledby="share_info"
              name="share"
              value={data.share || '0'}
              onChange={editarShare}
            >
              {/* Usamos Grid para controlar perfectamente el layout */}
              <Grid container spacing={3} sx={{ justifyContent: 'space-between' }} size={12}>
                {[
                  { value: '1', label: 'Todos', ayuda: 'Cualquier persona que entre a mi página' },
                  { value: '2', label: 'Miembros', ayuda: 'Miembros registrados a OneService' },
                  { value: '3', label: 'Conexiones', ayuda: 'Miembros con quienes he trabajado' },
                  { value: '0', label: 'Nadie', ayuda: 'Nadie podrá ver mis datos de contacto' },
                ].map((opcion) => (
                  <Grid key={opcion.value} size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControlLabel
                      value={opcion.value}
                      control={<Radio sx={{ color: "text.secondary" }} />}
                      label={opcion.label}
                      sx={{
                        margin: 0,
                        '& .MuiFormControlLabel-label': { fontWeight: 500 }
                      }}
                    />
                    <FormHelperText className='help-input' sx={{ fontSize: '0.7rem' }}>
                      {opcion.ayuda}
                    </FormHelperText>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </FormControl>
        </Grid>
      </Paper>

      {/* Delete Account Section */}
      <Grid size={12} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="outlined" 
          color="error" 
          onClick={() => setOpenDeleteDialog(true)}
          sx={{ borderRadius: '20px', px: 4 }}
        >
          Eliminar mi cuenta
        </Button>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" color="error.oscuro">
          {"¿Estás seguro de eliminar tu cuenta?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Esta acción es irreversible. Se borrarán tus datos personales de forma permanente y 
            perderás el acceso a la plataforma. Todos tus servicios y páginas serán dados de baja.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={() => {
            setOpenDeleteDialog(false);
            dispatch(deleteAccount(navigate));
          }} color="error" variant="contained" autoFocus>
            Eliminar definitivamente
          </Button>
        </DialogActions>
      </Dialog>

    </Grid>
  );
};

export default MainPerfil;